import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import Nav from './Nav';
import { Explain } from './Explain';
import FeedManager from './FeedManager';
import ArrangeBar from './ArrangeBar';
import ViewsetInline from './ViewsetInline';
import StreamRow from './StreamRow';
import { useLensWeights } from './lensWeights';
import { snip } from './snip';
import './css/trust.css';

// The merged HOME. The front door is a row of view pills over one
// (filter, ranking rule) surface — "every view is a viewset + a ranking rule"
// (ROADMAP, owner idea 2026-07-28) at page level.
//
// It arrived in two goes. 2026-08-05 (owner ruling) merged /viewsets in: the
// reading queue became a PRESET pill beside the viewer's own viewsets, and the
// definition editor moved inline behind ⚙. 2026-08-07 finished the item
// (HANDOFF 0c) by folding in the two remaining page-shaped ideas:
//
//   /resolve  was never a page. It was this surface under the `contested`
//             ranking rule, which is now a pill in the ArrangeBar.
//   Browse    was never a page either. It was this surface with NO filter
//             under the `new` rule, plus a tag narrowing and a search box,
//             both of which are query parameters here.
//
// Four page-shaped ideas are now one primitive with two axes. Both old routes
// redirect (index.js) and carry their query strings, so existing links land on
// the pill that means what the page meant.
//
// ── THE ONE THING THAT DID NOT FOLD, and why that is not a cop-out ──────────
// The reading queue keeps its own endpoint (/api/queue) and its own hot|new
// pair. Its rows are DOCUMENTS — title, outlet, claim count, unread dot — and
// the stream's rows are CLAIMS. That is a difference of GRAIN, not of filter,
// so collapsing it would mean rendering a document as a claim or vice versa.
// ROADMAP notes the queue's identity is a hardcoded derived_from → link join
// that the refactor should eventually dissolve into a filter; doing that needs
// a document-grain row in the same list, which is its own decision. Filed, not
// smuggled in here.

const QUEUE_SORTS = [
  { key: 'hot', label: 'hot', hint: 'most talk first — engagement ranks the lobby, never a trust score' },
  { key: 'new', label: 'new', hint: 'most recently imported first' },
];

// Shown until /api/stream answers with the real set. The server is the
// authority (it ships `sorts` in every response, so a pill can never advertise
// a rule the route does not implement) — this only stops the bar flashing
// empty on first paint and between pill switches.
const FALLBACK_SORTS = [
  { key: 'contested', label: 'contested', hint: 'widest named tension first' },
  { key: 'needs_judgment', label: 'needs judgment', hint: 'unjudged first' },
  { key: 'new', label: 'new', hint: 'most recently added first' },
  { key: 'strongest', label: 'strongest', hint: 'highest combined score first' },
  { key: 'hot', label: 'hot', hint: 'most attention first' },
];

function Queue() {
  const { user } = useAuth();
  const { weights } = useLensWeights();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── which view ──
  // No params at all = the reading queue, which keeps the front door's default
  // and every deep link that predates the fold.
  const activeViewset = searchParams.get('viewset');
  const isEverything = searchParams.get('view') === 'everything';
  // A THREAD'S CONTENTS as a stream (2026-08-08). Its own filter rather than a
  // viewset, because thread membership is a transitive closure over claimed
  // connectors and a viewset filter is one hop. Reached from the thread page and
  // from the MCP review_url, never from a pill: it is a view OF one thread, not
  // a standing slice of the graph.
  const threadFilter = searchParams.get('thread');
  const isQueue = !activeViewset && !isEverything && !threadFilter;
  const streamSort = searchParams.get('sort') || 'contested';
  const tagParam = searchParams.get('tags') || '';
  const query = searchParams.get('q') || '';

  const setParams = (next) => {
    const merged = { ...Object.fromEntries(searchParams), ...next };
    Object.keys(merged).forEach((k) => {
      if (!merged[k]) delete merged[k];
    });
    setSearchParams(merged, { replace: false });
  };

  // ── the pill row: presets + your viewsets ──
  const [viewsets, setViewsets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPublic, setNewPublic] = useState(false);
  const [createError, setCreateError] = useState(null);

  // How the PILL ROW itself is ordered (owner ruling 2026-08-08: every ranking
  // rule available on viewsets, hot included). This is a second, independent
  // (list, rule) pair from the one below — that one orders the CONTENT of the
  // selected view, this one orders the list of views. Kept in component state
  // rather than the URL on purpose: it is a property of how you are looking at
  // your own shelf, not of the view you are sharing a link to.
  const [vsSort, setVsSort] = useState('new');
  const [vsSorts, setVsSorts] = useState(null);
  // Which tag narrows the pill row, or null for all of them. The other half of
  // the owner's organizing ask — reordering answers "which first", tags answer
  // "which at all" once the list is long enough that reordering is not enough.
  const [vsTag, setVsTag] = useState(null);

  const loadViewsets = useCallback(() => {
    if (!user) return;
    // `.viewsets` since 2026-08-08 — the rule set ships with the rows so this
    // row cannot advertise a rule the route falls through to the default on.
    apiFetch(`/api/viewsets?sort=${encodeURIComponent(vsSort)}`)
      .then((r) => r.json())
      .then((d) => {
        setViewsets(Array.isArray(d?.viewsets) ? d.viewsets : []);
        if (Array.isArray(d?.sorts)) setVsSorts(d.sorts);
      })
      .catch(() => {});
  }, [user, vsSort]);
  useEffect(() => { loadViewsets(); }, [loadViewsets]);

  // Every tag in use across the viewer's viewsets, for the narrowing row.
  const vsTagNames = Array.from(
    new Set(viewsets.flatMap((vs) => (vs.tags || []).map((t) => t.content)))
  ).sort();
  const shownViewsets = vsTag
    ? viewsets.filter((vs) => (vs.tags || []).some((t) => t.content === vsTag))
    : viewsets;

  // A removed viewset must not leave the surface pointing at it. Falls back to
  // the reading queue, which is the front door's default with no params at all.
  const handleViewsetDeleted = () => {
    loadViewsets();
    selectView({});
  };

  // Switching pills drops the NARROWINGS and keeps the RANKING RULE. `tags`
  // and `q` narrow the view you were in, so carrying them into the next pill
  // would show a filtered list that looks like the whole thing. The rule is the
  // other axis rather than a narrowing — if you are reading by `new`, you meant
  // that about how you are reading, not about which view you were in.
  const selectView = (next) => {
    setCreating(false);
    const rule = searchParams.get('sort');
    setSearchParams(rule ? { ...next, sort: rule } : next, { replace: false });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const res = await apiFetch('/api/viewsets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), is_public: newPublic }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setNewName('');
      setNewPublic(false);
      setCreating(false);
      setCreateError(null);
      loadViewsets();
      selectView({ viewset: created.id }); // lands with the editor unfolded (empty viewset)
    } catch {
      setCreateError('Failed to create viewset');
    }
  };

  // ── the reading-queue preset (unchanged) ──
  const [sort, setSort] = useState('hot');
  const [docs, setDocs] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFeeds, setShowFeeds] = useState(false);
  const seenMarked = React.useRef(false);

  const loadDocs = useCallback(async (s) => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/queue?sort=${s}`);
      const data = await res.json();
      setDocs(data.documents || []);
      // unread watermark (migration 008): keep this visit's is_new flags,
      // advance the watermark ONCE per visit (bell pattern) — the dots stay
      // for the session you're looking at, the next visit starts fresh
      if (!seenMarked.current) {
        setNewCount(data.new_count || 0);
        seenMarked.current = true;
        if (data.new_count > 0) {
          apiFetch('/api/queue/seen', { method: 'POST' }).catch(() => {});
        }
      }
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isQueue) loadDocs(sort);
  }, [sort, loadDocs, isQueue]);

  // ── the stream: everything, and every viewset ──
  const [stream, setStream] = useState(null);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [sorts, setSorts] = useState(FALLBACK_SORTS);
  // Chip exclusions are the PARENT's because they are a query parameter of the
  // list, not a property of the definition — ViewsetInline renders the chips
  // and reports the toggles up.
  const [excludedFilters, setExcludedFilters] = useState(() => new Set());
  // Keep the identity stable when there is nothing to clear. A fresh empty Set
  // here would change loadStream's identity and fetch the same page twice on
  // every pill switch.
  useEffect(() => {
    setExcludedFilters((prev) => (prev.size ? new Set() : prev));
  }, [activeViewset]);

  const PAGE = 30;
  const [page, setPage] = useState(0);
  // Any change to the filter or the rule invalidates the page number — page 3
  // of one list is not page 3 of another.
  useEffect(() => {
    setPage(0);
  }, [activeViewset, isEverything, threadFilter, streamSort, tagParam, query, excludedFilters]);

  const loadStream = useCallback(async (withSpinner = true) => {
    if (withSpinner) setStreamLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('sort', streamSort);
      qs.set('limit', String(PAGE));
      if (page) qs.set('offset', String(page * PAGE));
      if (activeViewset) qs.set('viewset', activeViewset);
      if (threadFilter) qs.set('thread', threadFilter);
      if (excludedFilters.size) qs.set('exclude', [...excludedFilters].join(','));
      if (tagParam) qs.set('tags', tagParam);
      if (query) qs.set('q', query);
      const res = await apiFetch(`/api/stream?${qs.toString()}`);
      if (!res.ok) throw new Error('Failed to load the stream');
      const data = await res.json();
      setStream(data);
      if (Array.isArray(data.sorts) && data.sorts.length) setSorts(data.sorts);
      setStreamError(null);
    } catch (err) {
      setStreamError(err.message);
    } finally {
      setStreamLoading(false);
    }
  }, [streamSort, activeViewset, threadFilter, excludedFilters, tagParam, query, page]);

  useEffect(() => {
    if (!isQueue) loadStream();
  }, [isQueue, loadStream]);

  const toggleFilterChip = (filterId) =>
    setExcludedFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filterId)) next.delete(filterId);
      else next.add(filterId);
      return next;
    });

  // ── tag narrowing, carried over from Browse ────────────────────────────────
  // Kept rather than answered with "make a viewset": looking at everything
  // carrying a tag right now is a different act from defining a saved view, and
  // Browse was the only place you could do it.
  const [allTags, setAllTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  useEffect(() => {
    if (!isEverything) return;
    apiFetch('/api/objects?object_type=tag')
      .then((r) => r.json())
      .then((t) => setAllTags(Array.isArray(t) ? t : []))
      .catch(() => {});
  }, [isEverything]);

  const selectedTagIds = tagParam ? tagParam.split(',').filter(Boolean) : [];
  const selectedTags = selectedTagIds
    .map((tid) => allTags.find((t) => t.id === tid))
    .filter(Boolean);
  const availableTags = allTags.filter(
    (t) =>
      !selectedTagIds.includes(t.id) &&
      tagSearch.trim() &&
      t.content.toLowerCase().includes(tagSearch.toLowerCase())
  );
  const addTag = (tag) => {
    setParams({ tags: [...selectedTagIds, tag.id].join(',') });
    setTagSearch('');
  };
  const removeTag = (tagId) =>
    setParams({ tags: selectedTagIds.filter((t) => t !== tagId).join(',') });

  // Search is debounced into the URL, so it is deep-linkable and survives a
  // reload like every other narrowing here.
  const [searchDraft, setSearchDraft] = useState(query);
  useEffect(() => { setSearchDraft(query); }, [query]);
  useEffect(() => {
    if (searchDraft === query) return;
    const t = setTimeout(() => setParams({ q: searchDraft }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const pill = (label, { key, active, onClick, dashed, title } = {}) => (
    <button
      key={key}
      onClick={onClick}
      className={`wv-viewpill${active ? ' on' : ''}${dashed ? ' newvs' : ''}`}
      aria-pressed={!!active}
      title={title}
    >
      {label}
    </button>
  );

  return (
    <div>
      <Nav />
      {/* No page heading, by owner call 2026-08-07: the nav highlight already
          says where you are, and an <h1> reading "Home" above a row of pills
          that name the actual view was saying it twice. The pill row is the
          heading now — it is the thing that changes. The unread count moved
          down beside the queue's own controls, and the reading-queue ⓘ moved
          with it, since the heading was carrying it. */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'var(--wv-sp-6) var(--wv-sp-8) 40px' }}>
        {!user && (
          <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-md)' }}>
            <Link to="/login">Log in</Link> to vote on connections.
          </p>
        )}

        {/* the view pills — each view is a viewset + a ranking rule */}
        {user && (
          <div className="wv-viewrow" role="group" aria-label="views">
            {pill('reading queue', {
              active: isQueue,
              onClick: () => selectView({}),
              title: 'imported documents to read — the lobby preset',
            })}
            {pill('everything', {
              active: isEverything,
              onClick: () => selectView({ view: 'everything' }),
              title: 'every object you can see, under any ranking rule',
            })}
            {shownViewsets.length > 0 && <span className="wv-viewsep" aria-hidden="true" />}
            {shownViewsets.map((vs) =>
              pill(vs.name, {
                key: vs.id,
                active: activeViewset === vs.id,
                onClick: () => selectView({ viewset: vs.id }),
                title: [
                  `${vs.is_public ? 'public' : 'private'} · by ${vs.creator_username}`,
                  (vs.tags || []).length ? (vs.tags || []).map((t) => t.content).join(', ') : null,
                  vs.member_tension ? `contains: ${vs.member_tension.replace(/_/g, ' ')}` : null,
                ].filter(Boolean).join(' · '),
              })
            )}
            {pill('+ new', {
              dashed: true,
              active: creating,
              onClick: () => setCreating(!creating),
              title: 'create a viewset',
            })}
          </div>
        )}

        {/* ── ORDERING AND NARROWING THE PILL ROW ITSELF ──────────────────
            Owner ruling 2026-08-08: every ranking rule available on viewsets,
            `hot` included — *"I understand that people might gravitate towards
            hot, but I don't mind that because I'm banking on them also using
            the other views."*

            It stays inside the two bounds in backend/lib/engagement.ts because
            of what it structurally IS: a rule selector over the COMPLETE list.
            It reorders and never shortens, and `hot` is not the default. Only
            shown once there are enough viewsets for ordering to mean anything —
            a rule selector above two pills is furniture. */}
        {user && viewsets.length > 2 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-3)', flexWrap: 'wrap', marginBottom: 'var(--wv-sp-5)', fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--wv-sp-2)' }}>
              order
              <select
                value={vsSort}
                onChange={(e) => setVsSort(e.target.value)}
                style={{ fontSize: 'var(--wv-fs-xs)' }}
                title="How to order your viewsets. The signal rules read what each viewset CONTAINS — a viewset itself carries no trust score, because an arrangement is not an assertion."
              >
                {(vsSorts || FALLBACK_SORTS).map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </label>
            {vsTagNames.length > 0 && (
              <>
                <span className="wv-viewsep" aria-hidden="true" />
                <button
                  className={`wv-chip${vsTag === null ? ' on' : ''}`}
                  onClick={() => setVsTag(null)}
                  aria-pressed={vsTag === null}
                >
                  all
                </button>
                {vsTagNames.map((t) => (
                  <button
                    key={t}
                    className={`wv-chip${vsTag === t ? ' on' : ''}`}
                    onClick={() => setVsTag(vsTag === t ? null : t)}
                    aria-pressed={vsTag === t}
                    style={{ color: vsTag === t ? 'var(--wv-accent)' : undefined }}
                  >
                    {t}
                  </button>
                ))}
              </>
            )}
          </div>
        )}

        {/* inline create — the + new fold */}
        {creating && (
          <form onSubmit={handleCreate} className="wv-panel" style={{ marginBottom: 'var(--wv-sp-6)' }}>
            {createError && <div style={{ color: 'var(--wv-neg)', marginBottom: 'var(--wv-sp-3)' }}>{createError}</div>}
            <div style={{ display: 'flex', gap: 'var(--wv-sp-4)', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Viewset name…"
                style={{ flex: 1, minWidth: '180px' }}
                autoFocus
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--wv-fs-md)', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={newPublic} onChange={(e) => setNewPublic(e.target.checked)} />
                Public
              </label>
              <Explain concept="viewset-visibility" />
              <button type="submit" disabled={!newName.trim()}>Create</button>
            </div>
          </form>
        )}

        {isQueue ? (
          <>
            {/* the queue's own arrangement: hot|new + the ⚙ feeds fold */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--wv-sp-3)' }}>
              <div style={{ flex: 1 }}>
                <ArrangeBar sorts={QUEUE_SORTS} sort={sort} onSort={setSort} />
              </div>
              {newCount > 0 && (
                <span
                  className="wv-chip"
                  style={{ color: 'var(--wv-accent)', marginTop: 'var(--wv-sp-2)', flexShrink: 0 }}
                  title="Imported since you last looked at the queue"
                >
                  {newCount} new since you looked
                </span>
              )}
              <span style={{ marginTop: 'var(--wv-sp-2)', flexShrink: 0 }}>
                <Explain concept="reading-queue" />
              </span>
              {user && (
                <button
                  onClick={() => setShowFeeds(!showFeeds)}
                  aria-expanded={showFeeds}
                  style={{
                    background: 'none',
                    border: '1px solid var(--wv-border)',
                    borderRadius: 'var(--wv-r-pill)',
                    color: showFeeds ? 'var(--wv-accent)' : 'var(--wv-muted)',
                    fontSize: 'var(--wv-fs-xs)',
                    padding: '2px 9px',
                    lineHeight: 1.6,
                    cursor: 'pointer',
                    marginTop: 'var(--wv-sp-2)',
                    flexShrink: 0,
                  }}
                >
                  ⚙ feeds
                </button>
              )}
            </div>

            {user && showFeeds && (
              <div style={{ marginBottom: 'var(--wv-sp-8)' }}>
                <FeedManager onRefreshed={() => loadDocs(sort)} />
              </div>
            )}

            {loading ? (
              <p>Loading…</p>
            ) : docs.length === 0 ? (
              <p style={{ color: 'var(--wv-muted)' }}>
                Nothing to read yet. Subscribe to a feed (⚙ feeds), or add a page in{' '}
                <Link to="/write">Write</Link>.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {docs.map((d) => (
                  <li key={d.id} className="wv-card">
                    <Link to={`/read/${d.id}`} className="wv-plainlink" style={{ color: 'var(--wv-text)', fontWeight: 600, fontSize: 'var(--wv-fs-lg)' }}>
                      {d.is_new && (
                        <span title="New since you last looked" style={{ color: 'var(--wv-accent)', marginRight: 'var(--wv-sp-2)' }}>●</span>
                      )}
                      {d.title || '(untitled)'}
                    </Link>
                    {/* machine facts in the data voice (blend spec: queue meta is mono) */}
                    <div className="wv-data" style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)', marginTop: 'var(--wv-sp-1)' }}>
                      {d.outlet ? `${d.outlet} · ` : ''}
                      <Explain concept="decomposition">
                        {d.claim_count > 0
                          ? `${d.claim_count} claim${d.claim_count === 1 ? '' : 's'}${d.anchored_count ? ` (${d.anchored_count} anchored)` : ''}`
                          : 'not yet decomposed'}
                      </Explain>
                      {' · '}
                      {d.comment_count > 0 ? `${d.comment_count} comment${d.comment_count === 1 ? '' : 's'}` : 'no talk yet'}
                      {' · '}
                      <Explain concept="asserted-at">
                        {new Date(d.asserted_at || d.created_at).toLocaleDateString()}
                      </Explain>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            {/* A viewset renders its definition — chips and the ⚙ editor. The
                `everything` pill has no definition, so it gets the bare
                ranking bar plus the two narrowings Browse used to own. */}
            {activeViewset ? (
              <ViewsetInline
                id={activeViewset}
                sorts={sorts}
                sort={streamSort}
                onSort={(s) => setParams({ sort: s })}
                excludedFilters={excludedFilters}
                onToggleFilter={toggleFilterChip}
                onDefinitionChanged={() => { loadStream(false); loadViewsets(); }}
                onDeleted={handleViewsetDeleted}
              />
            ) : (
              <>
                <ArrangeBar sorts={sorts} sort={streamSort} onSort={(s) => setParams({ sort: s })} />

                <div style={{ margin: 'var(--wv-sp-4) 0' }}>
                  <input
                    type="text"
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    placeholder="Search content…"
                    style={{ width: '100%', padding: 'var(--wv-sp-3)', boxSizing: 'border-box', fontSize: 'var(--wv-fs-md)' }}
                  />
                </div>

                <div style={{ position: 'relative', marginBottom: 'var(--wv-sp-5)' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--wv-sp-2)', alignItems: 'center' }}>
                    {selectedTags.map((tag) => (
                      <span key={tag.id} className="wv-chip wv-tint">
                        {tag.content}
                        <button
                          onClick={() => removeTag(tag.id)}
                          title={`stop narrowing by ${tag.content}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 0, minHeight: 0, lineHeight: 1, color: 'var(--wv-muted)' }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={tagSearch}
                      onChange={(e) => setTagSearch(e.target.value)}
                      placeholder={selectedTags.length ? 'narrow further…' : 'narrow by tag…'}
                      style={{ flex: 1, minWidth: '160px', fontSize: 'var(--wv-fs-sm)' }}
                    />
                  </div>
                  {availableTags.length > 0 && (
                    <div className="wv-dropdown">
                      {availableTags.slice(0, 8).map((tag) => (
                        <div key={tag.id} onClick={() => addTag(tag)} className="wv-dropdown-item">
                          {tag.content}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedTags.length > 1 && (
                    <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
                      tagged with all {selectedTags.length}
                    </div>
                  )}
                </div>
              </>
            )}

            {streamLoading && <p>Loading…</p>}
            {streamError && <p style={{ color: 'var(--wv-neg)' }}>{streamError}</p>}
            {stream && !streamLoading && (
              <>
                {stream.items.length === 0 && (
                  <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-ui)' }}>
                    {query || selectedTags.length
                      ? 'Nothing here matches that narrowing.'
                      : stream.showing_no_signal
                        ? 'Nothing here yet.'
                        : 'Nothing here carries a trust signal yet — votes on connections are what light content up.'}
                  </p>
                )}
                {stream.items.map((item) => (
                  <StreamRow
                    key={item.id}
                    item={item}
                    weights={weights}
                    onStanceChanged={() => loadStream(false)}
                  />
                ))}

                {/* Paging exists because Browse folded in here and Browse could
                    reach everything. Saying the total out loud matters more
                    than the buttons: a capped list with no count reads as the
                    whole set, which is the one thing a view must not do. */}
                {stream.total > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--wv-sp-4)',
                      marginTop: 'var(--wv-sp-6)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span className="trust-muted wv-data" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                      {stream.offset + 1}–{stream.offset + stream.items.length} of {stream.total}
                    </span>
                    <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                      ← previous
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={stream.offset + stream.items.length >= stream.total}
                    >
                      next →
                    </button>
                  </div>
                )}

                {stream.staked_unexamined?.length > 0 && (
                  <div style={{ marginTop: 'var(--wv-sp-8)' }}>
                    <h3 style={{ fontSize: 'var(--wv-fs-md)', margin: '0 0 var(--wv-sp-2)' }}>
                      <Explain
                        title="Staked, unexamined"
                        text="Claims people have staked trust or distrust on, with no evidence connected yet — credence without argument. A work-list, not a ranking: it never enters the tension order (one-sided staking must not buy placement), and it is ordered by how many people are waiting on evidence. Connect a supporting or contradicting claim to move one into the judged graph."
                      >
                        Staked, unexamined
                      </Explain>
                    </h3>
                    {stream.staked_unexamined.map((s) => (
                      <div key={s.id} className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', padding: 'var(--wv-sp-1) 0' }}>
                        <Link to={`/object/${s.id}`} style={{ color: 'inherit' }}>{snip(s.content, 110)}</Link>{' '}
                        <span style={{ whiteSpace: 'nowrap' }}>
                          — {s.pos > 0 && `${s.pos} trust`}{s.pos > 0 && s.neg > 0 && ' · '}{s.neg > 0 && `${s.neg} distrust`}, no evidence yet
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Under a rule that HIDES them this is a remainder and points
                    somewhere else; under one that shows them it is a count of
                    what you are already looking at. Saying the same sentence in
                    both cases would be wrong in one of them. */}
                {stream.no_signal_count > 0 && !stream.showing_no_signal && (
                  <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', marginTop: 'var(--wv-sp-6)' }}>
                    <Explain concept="no-signal">
                      {stream.no_signal_count} more object{stream.no_signal_count === 1 ? '' : 's'} with no
                      trust signal yet
                    </Explain>{' '}
                    — switch to{' '}
                    <button
                      onClick={() => setParams({ sort: 'needs_judgment' })}
                      className="wv-plainlink"
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--wv-accent)', cursor: 'pointer', font: 'inherit' }}
                    >
                      needs judgment
                    </button>{' '}
                    to work through them.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Queue;
